import type { CSSProperties, ReactElement } from 'react';
import {
  isGameSeed,
  type ConversationSeed,
  type GameBand,
  type GameScore,
} from '@orborix/shared-types';
import { directionForSeed } from './directionsData.js';
import { IconCheck, IconX } from '../icons.js';
import type { GameBandMoment, GameDifficultyAction } from '../hooks/useGameArena.js';
import '../gameArena.css';

/**
 * The SCORE PILL of game mode (plan 47 Phase 2; evolved from the old "Modo
 * juego" chip): while a game seed is active it makes the game frame visible,
 * under the header, in the game's colour and glyph.
 *
 * - Every game: the game's name and a visible **Salir** (`clearSeed`).
 * - Every game but describe-it: **Más fácil / Más difícil**
 *   (`sendGameAction`), one notch from now on — the difficulty lives on
 *   screen, the coach no longer asks.
 * - translate-it (the only game with round state): «Ronda N», the ✓ count
 *   and the streak of THIS game (`gameScoreOf` over the thread's stamps).
 *   Without a meaning check for the session (`scored: false`) nothing is ever
 *   stamped, so only «Ronda N» stays.
 * - translate-it (Round 2): the challenges' BAND («B1+», decided in code)
 *   next to Salir, and for a few seconds after the band moves down by itself
 *   a «↓ Bajamos a B1» moment in its place. A manual shift (Más fácil / Más
 *   difícil) just shows the new band. The band never climbs on its own: after
 *   a run of «¡Perfecto!» a coach-mark points at Más difícil
 *   (`ConversationCoachmarks`, anchored on `.game-pill__harder`).
 *
 * Stateless: everything comes from `useGameArena` through the controller. In
 * describe-it it sits above the `SecretWordCard` (the pill says which game,
 * the card shows the round's material).
 */
interface GameModeChipProps {
  seed: ConversationSeed;
  onExit: () => void;
  /** The session ended: Salir would go to a dead socket. */
  exitDisabled?: boolean;
  /** translate-it: the current round number (`null` before the first challenge). */
  round?: number | null;
  /** translate-it: this game's score. */
  score?: GameScore | null;
  /** translate-it: the session can stamp attempts; `false` hides ✓ and streak. */
  scored?: boolean;
  difficulty?: {
    onPick: (action: GameDifficultyAction) => void;
    disabled: boolean;
    pending: GameDifficultyAction | null;
  } | null;
  /** translate-it: the current challenges' band (`null` = unknown / before the first). */
  band?: GameBand | null;
  /** translate-it: the band just moved down by itself. */
  bandMoment?: GameBandMoment | null;
}

/** Learner-facing copy. */
export const GAME_PILL_COPY = {
  kicker: 'Modo juego',
  exit: 'Salir',
  exitAria: 'Salir del juego',
  round: 'Ronda',
  streak: 'racha',
  easier: 'Más fácil',
  harder: 'Más difícil',
  difficulty: 'Dificultad',
  band: 'Nivel',
  bandAria: (band: string) => `Nivel de las frases: ${band}`,
  bandDown: (band: string) => `Bajamos a ${band}`,
} as const;

export function GameModeChip({
  seed,
  onExit,
  exitDisabled = false,
  round = null,
  score = null,
  scored = true,
  difficulty = null,
  band = null,
  bandMoment = null,
}: GameModeChipProps): ReactElement | null {
  const dir = directionForSeed(seed);
  if (!dir || !isGameSeed(seed)) return null;
  const Glyph = dir.Glyph;
  // Unscored, the stats are only the round: nothing to show before the first one.
  const showStats = score !== null && (scored || (round !== null && round > 0));
  return (
    <div className="game-pill-bar">
      <div
        className="game-pill"
        style={{ '--dir-color': dir.color } as CSSProperties}
        role="group"
        aria-label={`Modo juego: ${dir.chip}`}
      >
        <div className="game-pill__row">
          <span className="game-pill__glyph" aria-hidden="true">
            <Glyph size={14} />
          </span>
          <span className="game-pill__titles">
            <span className="game-pill__kicker">{GAME_PILL_COPY.kicker}</span>
            <span className="game-pill__name">{dir.chip}</span>
          </span>
          {bandMoment ? (
            <span key={bandMoment.key} className="game-pill__band-moment is-down" role="status">
              <span aria-hidden="true">↓</span>
              {GAME_PILL_COPY.bandDown(bandMoment.band)}
            </span>
          ) : (
            band && (
              <span className="game-pill__band" role="img" aria-label={GAME_PILL_COPY.bandAria(band)}>
                <span className="game-pill__band-label" aria-hidden="true">
                  {GAME_PILL_COPY.band}
                </span>
                <b aria-hidden="true">{band}</b>
              </span>
            )
          )}
          <button
            type="button"
            className="game-pill__exit"
            onClick={onExit}
            disabled={exitDisabled}
            aria-label={GAME_PILL_COPY.exitAria}
          >
            <IconX size={12} />
            {GAME_PILL_COPY.exit}
          </button>
        </div>
        {(showStats || difficulty) && (
          <div className="game-pill__row">
            {showStats ? (
              <span className="game-pill__stats" role="status" aria-live="polite">
                {round !== null && round > 0 && (
                  <span className="game-pill__stat">
                    {GAME_PILL_COPY.round} <b>{round}</b>
                  </span>
                )}
                {scored && (
                  <span className="game-pill__stat is-clean" aria-label={`${score.clean} bien`}>
                    <IconCheck size={11} />
                    <b>{score.clean}</b>
                  </span>
                )}
                {scored && score.streak >= 2 && (
                  <span
                    key={score.streak}
                    className={`game-pill__stat is-streak${score.streak >= 3 ? ' is-hot' : ''}`}
                  >
                    {GAME_PILL_COPY.streak} <b>×{score.streak}</b>
                  </span>
                )}
              </span>
            ) : (
              <span className="game-pill__diff-label">{GAME_PILL_COPY.difficulty}</span>
            )}
            {difficulty && (
              <span className="game-pill__diff">
                <button
                  type="button"
                  className={difficulty.pending === 'easier' ? 'is-pending' : undefined}
                  disabled={difficulty.disabled}
                  onClick={() => difficulty.onPick('easier')}
                >
                  − {GAME_PILL_COPY.easier}
                </button>
                <button
                  type="button"
                  className={`game-pill__harder${difficulty.pending === 'harder' ? ' is-pending' : ''}`}
                  disabled={difficulty.disabled}
                  onClick={() => difficulty.onPick('harder')}
                >
                  {GAME_PILL_COPY.harder} +
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
